import moment from 'moment';

import { I18N_FORMAT_MAP } from '@rover/kibble/styles';
import { ServiceType as RSDKServiceType } from '@rover/rsdk/src/apiClient/latest';
import {
  DATE_FIELDS,
  DOG_SIZE_FIELD,
  EMPTY_ALLOWABLE_FIELDS,
  PET_SIZES_FIELDS,
  PROVIDE_AS_ARRAY_IN_QUERY_FIELDS,
  SPACES_REQUIRED_SUPPORTED_SERVICES,
} from '@rover/shared/js/constants/searchPage.constants';
import { SERVICE_TYPE_CHOICES } from '@rover/shared/js/constants/service';
import type { MemberProfileSearchFilters } from '@rover/types/src/MemberProfileSearchFilters';
import { ServiceFrequencyEnum } from '@rover/types/src/ServiceFrequency';
import type { ServiceType, ServiceTypeSlug } from '@rover/types/src/ServiceType';

import type {
  BusinessTypeProps,
  DateRangeProps,
  DaycareTypeProps,
  EndDateProps,
  FilterValue,
  QueryParams,
  SelectedDaysProps,
  SpacesRequiredProps,
  WithBooleanSelectedDays,
  WithBusinessType,
  WithDateRange,
  WithDaycareType,
  WithDogSize,
  WithIsAcceptingRecurringClients,
  WithManualFlags,
  WithOptionalEndDate,
  WithOptionalSpacesRequired,
  WithPage,
  WithServiceType,
  WithValidBounds,
} from './filtersToQuery.types';

// eslint-disable-next-line
export const isFalsyOrEmpty = (val: any): boolean =>
  (typeof val === 'string' && !val.length) ||
  typeof val === 'undefined' ||
  val === null ||
  (Array.isArray(val) && !val.length);

export const removeEmptyKeys = <T extends object>(filters: T): Partial<T> => {
  if (typeof filters !== 'object') return filters;
  const o = { ...filters };

  Object.keys(o).forEach(
    (k) => !EMPTY_ALLOWABLE_FIELDS.has(k) && isFalsyOrEmpty(o[k]) && delete o[k]
  );

  return o;
};

const setPage = <T>(filters: T, isDirty: boolean): T & WithPage => {
  const clone = <T & WithPage>{ ...filters };
  if (isDirty) clone.page = 1;
  return clone;
};

const setIsAcceptingNewRecurringClients = <T>(
  filters: T & DateRangeProps
): T & WithIsAcceptingRecurringClients => {
  if (filters.frequency === ServiceFrequencyEnum.REPEAT) {
    return {
      ...filters,
      isAcceptingNewRecurringClients: true,
    };
  }

  return filters;
};

const setManualFlags = <T>(filters: T): T & WithManualFlags => ({
  alternateResults: true,
  ...filters,
});

const petSizesToList = <T extends object>(filters: T): T & WithDogSize => {
  // Historically the web search page used pet size parameters in the format of
  // small_dogs=true. This is different from how the mobile apps work
  // and incompatible with the API. The API expects these parameters in the
  // format: dog_size=small. In order to support pages that might link to
  // search with the old small_dogs=true, and be compatible with the expected
  // API behavior, we convert the old style parameters to the newer style in
  // this  method.

  const o = { ...filters };
  o[DOG_SIZE_FIELD] = [];

  PET_SIZES_FIELDS.forEach((field) => {
    if (field in o && o[field]) {
      o[DOG_SIZE_FIELD].push(field.replace('Dogs', ''));
    }
  });

  if (!o[DOG_SIZE_FIELD]?.length) {
    delete o[DOG_SIZE_FIELD];
  }

  return o;
};

const maybeOmitSpacesRequired = <T>(
  filters: T & SpacesRequiredProps
): T & WithOptionalSpacesRequired => {
  const o = { ...filters };

  if (
    o.spacesRequired &&
    o.serviceType &&
    !SPACES_REQUIRED_SUPPORTED_SERVICES.has(o.serviceType as ServiceTypeSlug)
  ) {
    delete o.spacesRequired;
  }

  return o;
};

const convertDateRange = <T>(filters: T & DateRangeProps): T & WithDateRange => {
  const o = <T & DateRangeProps & WithDateRange>{ ...filters };

  if (o.dateRange && o.frequency === ServiceFrequencyEnum.ONE_TIME) {
    o.startDate = o.dateRange?.startDate;
    o.endDate = o.dateRange?.endDate;
    delete o.dateRange;
  }

  return o;
};

const maybeOmitEndDate = <T>(
  filters: T & EndDateProps,
  serviceType: ServiceType | RSDKServiceType | null | undefined
): T & WithOptionalEndDate => {
  const o = { ...filters };

  if (
    o.endDate &&
    o.serviceType &&
    o.frequency === ServiceFrequencyEnum.REPEAT &&
    serviceType?.supportsDayOfWeekSearch
  ) {
    delete o.endDate;
  }

  return o;
};

const datesToStrings = <T extends object>(filters: T, language: string): T => {
  const o = { ...filters };

  DATE_FIELDS.forEach((field) => {
    if (field in o) {
      const isoFormat = I18N_FORMAT_MAP[language].DATE_ISO;
      const getDateAtNoon = (date: Date): Date =>
        date && new Date(new Date(date).setHours(12, 0, 0, 0));
      const dateAtNoon = getDateAtNoon(o[field]);
      o[field] = o[field] ? moment.utc(dateAtNoon).format(isoFormat) : '';
    }
  });

  return o;
};

const selectedDaysToBool = <T>(
  filters: T & SelectedDaysProps
): T | (Omit<T, 'selectedDays'> & WithBooleanSelectedDays) => {
  const o = { ...filters };

  if (o.selectedDays) {
    if (o.frequency === ServiceFrequencyEnum.REPEAT) {
      o.selectedDays.forEach((day) => {
        o[day.name.toLowerCase()] = 'true';
      });
    }

    delete o.selectedDays;
  }

  return o;
};

const validateSearchBounds = <T>(filters: T & WithValidBounds): T & WithValidBounds => {
  const updatedFilters = { ...filters };

  const { minlat, maxlat, minlng, maxlng } = updatedFilters;
  const boundsHaveValue =
    minlat !== undefined && minlng !== undefined && maxlat !== undefined && maxlng !== undefined;
  const boundsMissingValue = !boundsHaveValue;
  const latOrLngEqual = minlat === maxlat || minlng === maxlng;

  if (boundsMissingValue || (boundsHaveValue && latOrLngEqual)) {
    delete updatedFilters.minlat;
    delete updatedFilters.minlng;
    delete updatedFilters.maxlat;
    delete updatedFilters.maxlng;
  }

  return updatedFilters;
};

const valueToQueryString = (filterName: string, filterValue: FilterValue): string | string[] => {
  if (Array.isArray(filterValue)) {
    const encodedValues = filterValue.map((value) => encodeURIComponent(value));
    return PROVIDE_AS_ARRAY_IN_QUERY_FIELDS.has(filterName)
      ? encodedValues
      : encodedValues.join(',');
  }

  const stringValue = filterValue === undefined || filterValue == null ? '' : `${filterValue}`;
  return encodeURIComponent(stringValue);
};

export const setDaycareType = <T extends object>(
  filters: T & DaycareTypeProps & WithDaycareType & WithServiceType
): Omit<T & WithDaycareType, 'inSittersHome' | 'atDaycareFacility'> => {
  const updatedFilters = { ...filters };

  const { inSittersHome, atDaycareFacility, serviceType } = updatedFilters;

  delete updatedFilters.inSittersHome;
  delete updatedFilters.atDaycareFacility;
  delete updatedFilters.daycareType;

  if (serviceType !== SERVICE_TYPE_CHOICES.dayCare) {
    return updatedFilters;
  }

  const daycareTypeArray: string[] = [];

  if (inSittersHome) {
    daycareTypeArray.push('sitter');
  }
  if (atDaycareFacility) {
    daycareTypeArray.push('facility');
  }

  if (daycareTypeArray.length > 0) updatedFilters.daycareType = daycareTypeArray;

  return updatedFilters;
};

export const maybeOmitBusinessType = <T extends object>(
  filters: T & BusinessTypeProps & WithDaycareType & WithServiceType
): T & WithBusinessType => {
  const updatedFilters = { ...filters };

  if (filters.serviceType !== SERVICE_TYPE_CHOICES.boarding) {
    delete updatedFilters.businessType;
  }

  return updatedFilters;
};

export const valuesToQueryStrings = <T extends object>(filters: T): QueryParams => {
  const clone = { ...filters };

  return Object.keys(clone).reduce(
    (acc, currentFilter) => ({
      ...acc,
      [currentFilter]: valueToQueryString(currentFilter, clone[currentFilter]),
    }),
    {}
  );
};

/**
 * @deprecated Opt to use the newer version `memberProfileFiltersToURLSearchParams` found here `src/frontend/shared/js/search/utilities/filtersToURLSearchParams/index.ts`
 */
export const memberProfileFiltersToLinkParams = (
  filters: MemberProfileSearchFilters,
  language: string
): Record<string, string | undefined> => {
  const withoutEmpty = removeEmptyKeys<MemberProfileSearchFilters>(filters);
  const withDateStrings = datesToStrings<typeof withoutEmpty>(withoutEmpty, language);
  const withBooleanSelectedDays = selectedDaysToBool<typeof withDateStrings>(withDateStrings);
  return valuesToQueryStrings<typeof withBooleanSelectedDays>(withBooleanSelectedDays);
};

/**
 * @deprecated Opt to use the newer version `filtersToURLSearchParams` found here `src/frontend/shared/js/search/utilities/filtersToURLSearchParams/index.ts`
 */
const filtersToQuery = <T extends object>(
  filters: T,
  serviceType: ServiceType | RSDKServiceType | null | undefined,
  isDirty: boolean,
  language: string
): QueryParams => {
  const withoutEmpty = removeEmptyKeys(filters);
  const withUpdatedPage = setPage<typeof withoutEmpty>(withoutEmpty, isDirty);
  const withManualFlags = setManualFlags<typeof withUpdatedPage>(withUpdatedPage);
  const withPetSizesAsList = petSizesToList<typeof withManualFlags>(withManualFlags);
  const withOptionalSpaces = maybeOmitSpacesRequired<typeof withPetSizesAsList>(withPetSizesAsList);
  const withDateRange = convertDateRange<typeof withOptionalSpaces>(withOptionalSpaces);
  const withOptionalEndDate = maybeOmitEndDate<typeof withDateRange>(withDateRange, serviceType);
  const withIsAcceptingRecurringClients =
    setIsAcceptingNewRecurringClients<typeof withOptionalEndDate>(withOptionalEndDate);
  const withDateStrings = datesToStrings<typeof withIsAcceptingRecurringClients>(
    withIsAcceptingRecurringClients,
    language
  );
  const withBoolDays = selectedDaysToBool<typeof withOptionalEndDate>(withDateStrings);
  const withValidBounds = validateSearchBounds<typeof withBoolDays>(withBoolDays);
  const withDaycareType = setDaycareType<typeof withValidBounds>(withValidBounds);
  const withBusinessType = maybeOmitBusinessType<typeof withDaycareType>(withDaycareType);

  const query = valuesToQueryStrings<typeof withBusinessType>(withBusinessType);
  return query;
};

export default filtersToQuery;
